import { supabase } from '../config/supabase';

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function toPayload(
  value: unknown,
  context?: { table: RoomScopedTable; rowId: string },
): Record<string, unknown> | null | undefined {
  if (value == null) {
    return value;
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  console.error(
    `[supabaseDataAdapter] Invalid payload type for ${context?.table ?? 'unknown table'}:${context?.rowId ?? 'unknown row'}; expected object/null, got ${typeof value}`,
  );
  return null;
}

type RoomScopedTable =
  | 'timer_room_timers'
  | 'timer_room_messages'
  | 'timer_room_message_forms'
  | 'timer_room_connections';

type RoomScopedIdColumnMap = {
  timer_room_timers: 'timer_id';
  timer_room_messages: 'message_id';
  timer_room_message_forms: 'form_id';
  timer_room_connections: 'connection_id';
};

const ROOM_SCOPED_ID_COL: RoomScopedIdColumnMap = {
  timer_room_timers: 'timer_id',
  timer_room_messages: 'message_id',
  timer_room_message_forms: 'form_id',
  timer_room_connections: 'connection_id',
};

export function makeId(): string {
  return randomId();
}

export async function upsertRoomScopedPayload(
  table: RoomScopedTable,
  roomId: string,
  itemId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const idCol = ROOM_SCOPED_ID_COL[table];
  const { error } = await supabase.from(table).upsert(
    {
      room_id: roomId,
      [idCol]: itemId,
      payload,
    },
    { onConflict: `room_id,${idCol}` },
  );
  if (error) throw error;
}

export async function mergeRoomScopedPayloadAtomic(
  table: RoomScopedTable,
  roomId: string,
  itemId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const idCol = ROOM_SCOPED_ID_COL[table];
  const { error } = await supabase.rpc('timer_merge_room_scoped_payload', {
    p_table: table,
    p_room_id: roomId,
    p_id_col: idCol,
    p_item_id: itemId,
    p_patch: patch,
  });
  if (error) throw error;
}

export async function deleteRoomScopedPayload(
  table: RoomScopedTable,
  roomId: string,
  itemId: string,
): Promise<void> {
  const idCol = ROOM_SCOPED_ID_COL[table];
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('room_id', roomId)
    .eq(idCol, itemId);
  if (error) throw error;
}

export async function fetchRoomScopedPayloadMap(
  table: RoomScopedTable,
  roomId: string,
): Promise<Record<string, Record<string, unknown> | null | undefined>> {
  const idCol = ROOM_SCOPED_ID_COL[table];
  const { data, error } = await supabase
    .from(table)
    .select(`${idCol},payload`)
    .eq('room_id', roomId);
  if (error) throw error;
  const out: Record<string, Record<string, unknown> | null | undefined> = {};
  for (const row of data ?? []) {
    const id = String((row as Record<string, unknown>)[idCol] ?? '');
    if (!id) continue;
    out[id] = toPayload((row as Record<string, unknown>).payload, { table, rowId: id });
  }
  return out;
}

/** Минимальный срез события postgres_changes: тип и новая строка (если Realtime её прислал). */
export interface RowChangeEvent {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  newRow: Record<string, unknown> | null;
}

function toRowChangeEvent(raw: unknown): RowChangeEvent {
  const r = (raw ?? {}) as { eventType?: string; new?: unknown };
  const eventType =
    r.eventType === 'INSERT' || r.eventType === 'UPDATE' || r.eventType === 'DELETE'
      ? r.eventType
      : 'UPDATE';
  const newRow =
    r.new && typeof r.new === 'object' && !Array.isArray(r.new) && Object.keys(r.new).length > 0
      ? (r.new as Record<string, unknown>)
      : null;
  return { eventType, newRow };
}

export function subscribeRoomScopedTable(
  table: RoomScopedTable,
  roomId: string,
  onChange: (event: RowChangeEvent) => void,
): () => void {
  const channel = supabase
    .channel(`${table}:${roomId}:${makeId()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `room_id=eq.${roomId}` },
      (raw) => onChange(toRowChangeEvent(raw)),
    )
    .subscribe();
  return () => {
    void channel.unsubscribe().finally(() => {
      void supabase.removeChannel(channel);
    });
  };
}

export function subscribeTableRow(
  table: string,
  idColumn: string,
  idValue: string,
  onChange: (event: RowChangeEvent) => void,
): () => void {
  const channel = supabase
    .channel(`${table}:${idColumn}:${idValue}:${makeId()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `${idColumn}=eq.${idValue}` },
      (raw) => onChange(toRowChangeEvent(raw)),
    )
    .subscribe();
  return () => {
    void channel.unsubscribe().finally(() => {
      void supabase.removeChannel(channel);
    });
  };
}
